"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useImperativeHandle, useState, } from "react";
import classNames from "classnames";
import { Shimmer } from "../../vfx";
import ErrorIcon from "@travel/shared/assets/icons/icon-error.svg";
import CloseIcon from "@travel/shared/assets/icons/icon-close.svg";
import styles from "./input.module.css";
export var Input = forwardRef(function Input(_a, inputRef) {
    var _b = _a.tag, tag = _b === void 0 ? "input" : _b, children = _a.children, rest = __rest(_a, ["tag", "children"]);
    var Tag = tag || "input";
    return (_jsx(BaseInput, __assign({}, rest, { ref: inputRef, children: function (_a) {
            var labelID = _a.labelID, id = _a.id, className = _a.className, value = _a.value, type = _a.type, maxLength = _a.maxLength, onFocus = _a.onFocus, onBlur = _a.onBlur, disabled = _a.disabled, placeholder = _a.placeholder, onChange = _a.onChange, onPaste = _a.onPaste, ref = _a.ref, inputProps = __rest(_a, ["labelID", "id", "className", "value", "type", "maxLength", "onFocus", "onBlur", "disabled", "placeholder", "onChange", "onPaste", "ref"]);
            return (_jsx(Tag, { id: id, className: className, value: value, type: type, placeholder: placeholder, disabled: disabled, children: children, maxLength: maxLength, onFocus: onFocus, onBlur: onBlur, onChange: onChange, onPaste: onPaste, "aria-labelledby": labelID, ref: ref }));
        } })));
});
export var BaseInput = forwardRef(function InputBase(_a, ref) {
    var _b, _c;
    var className = _a.className, classes = _a.classNames, _d = _a.type, type = _d === void 0 ? "text" : _d, valueProp = _a.value, focused = _a.focused, _e = _a.forceFocused, forceFocused = _e === void 0 ? false : _e, label = _a.label, placeholder = _a.placeholder, icon = _a.icon, _f = _a.size, size = _f === void 0 ? "md" : _f, _g = _a.error, error = _g === void 0 ? [] : _g, clear = _a.clear, disabled = _a.disabled, controls = _a.controls, _h = _a.loading, loading = _h === void 0 ? false : _h, horizontal = _a.horizontal, transparent = _a.transparent, loadingLabel = _a.loadingLabel, maxLength = _a.maxLength, _j = _a.iconTheme, iconTheme = _j === void 0 ? "primary" : _j, onChange = _a.onChange, onFocus = _a.onFocus, onBlur = _a.onBlur, onPaste = _a.onPaste, children = _a.children, rest = __rest(_a, ["className", "classNames", "type", "value", "focused", "forceFocused", "label", "placeholder", "icon", "size", "error", "clear", "disabled", "controls", "loading", "horizontal", "transparent", "loadingLabel", "maxLength", "iconTheme", "onChange", "onFocus", "onBlur", "onPaste", "children"]);
    var _k = __read(useState(valueProp), 2), value = _k[0], setValue = _k[1];
    var _l = __read(useState(!!focused), 2), isFocused = _l[0], setIsFocused = _l[1];
    var _m = __read(useState(false), 2), isValid = _m[0], setIsValid = _m[1];
    var inputRef = useRef(null);
    var errors = Array.isArray(error) ? error : [error];
    var hasErrors = errors.length > 0;
    var labelID = "".concat(rest.name, "-input-label");
    useImperativeHandle(ref, function () {
        return {
            el: inputRef.current,
            setValue: setValue,
            focus: function () {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                setIsFocused(true);
            },
            blur: function () {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                setIsFocused(false);
            },
        };
    }, []);
    useEffect(function () {
        setValue(valueProp);
    }, [valueProp]);
    useEffect(function () {
        setIsFocused(!!focused);
    }, [focused]);
    var containerClassNames = (_b = {},
        _b[styles["focused"]] = forceFocused || isFocused,
        _b[styles["has-value"]] = !!value,
        _b[styles["has-placeholder"]] = !!placeholder,
        _b[styles["has-icon"]] = !!icon,
        _b[styles["has-label"]] = !!label,
        _b[styles["has-error"]] = hasErrors,
        _b[styles["is-loading"]] = loading,
        _b[styles["size-".concat(size)]] = true,
        _b[styles["disabled"]] = !!disabled,
        _b[styles["horizontal"]] = !!horizontal,
        _b[styles["transparent"]] = !!transparent,
        _b);
    if (classes === null || classes === void 0 ? void 0 : classes.focused) {
        containerClassNames[classes === null || classes === void 0 ? void 0 : classes.focused] = isFocused;
    }
    if (classes === null || classes === void 0 ? void 0 : classes.hasError) {
        containerClassNames[classes === null || classes === void 0 ? void 0 : classes.hasError] = hasErrors;
    }
    return (_jsxs("div", { className: classNames(styles["container"], className, containerClassNames, (_c = {},
            _c[styles["has-clear"]] = !!clear,
            _c)), children: [(label || loadingLabel) && (_jsx("div", { id: labelID, className: styles["label"], children: _jsx("span", { className: styles["text"], children: loadingLabel && loading ? loadingLabel : label }) })), icon && (_jsx("div", { className: classNames(styles["icon"], styles[iconTheme], classes === null || classes === void 0 ? void 0 : classes.icon), children: icon })), loading && _jsx(Shimmer, { className: styles["loading"] }), children === null || children === void 0 ? void 0 : children(__assign(__assign({}, rest), { value: value, type: type, maxLength: maxLength, disabled: disabled, placeholder: placeholder, focused: forceFocused || isFocused, className: classNames(styles["input"], classes === null || classes === void 0 ? void 0 : classes.input), ref: inputRef, labelID: labelID, onChange: handleOnChange, onFocus: handleOnFocus, onBlur: handleOnBlur, onPaste: onPaste })), controls, !!clear && (_jsx("button", { type: "button", className: styles["clear"], onMouseDown: handleOnClickClear, children: clear === "x" ? _jsx(CloseIcon, {}) : clear === true ? "Clear" : clear })), errors.length > 0 && (_jsxs("div", { className: classNames(styles["errors"], classes === null || classes === void 0 ? void 0 : classes.errors), children: [_jsx(ErrorIcon, { className: styles["icon"] }), _jsx("ul", { className: styles["list"], children: errors.map(function (error, index) { return (_jsx("li", { className: classNames(styles["error"], classes === null || classes === void 0 ? void 0 : classes.error), children: error }, index)); }) })] }))] }));
    function handleOnChange(event) {
        var nextValue = event.currentTarget.value;
        if (event.currentTarget.validity.valid) {
            setValue(nextValue);
            onChange === null || onChange === void 0 ? void 0 : onChange(event);
        }
        else {
            setValue(value);
        }
    }
    function handleOnFocus(event) {
        setIsFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    }
    function handleOnBlur(event) {
        if (!focused) {
            setIsFocused(false);
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    }
    function handleOnClickClear(event) {
        var _a, _b;
        event.preventDefault();
        event.stopPropagation();
        (_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
        setValue("");
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
    }
});
